import type {
  FieldMapping,
  MappingRuleTemplateRef,
  TransformationStep,
} from '../../services/api';
import type { CanvasNode, CanvasNodeTemplateReference } from '../types';
import { buildQualified, normaliseQualifiedValue, parseQualifiedName } from './qualifiedNames';

export const splitSourceField = (field: string | undefined): string[] => {
  if (!field) return [];
  // Split by comma, trim, and filter empty
  return field.split(',').map((s) => s.trim()).filter((s) => s.length > 0);
};

export const normaliseMappingRuleTemplates = (
  templates?: MappingRuleTemplateRef[]
): CanvasNodeTemplateReference[] => {
  if (!templates || templates.length === 0) {
    return [];
  }
  const dedup = new Map<string, CanvasNodeTemplateReference>();
  templates.forEach((entry) => {
    if (!entry) {
      return;
    }
    const templateId = entry.template_id || entry.rule_id;
    if (!templateId) {
      return;
    }
    const versionValue = typeof entry.version === 'number' ? entry.version : Number(entry.version);
    const tags = Array.isArray(entry.tags) ? entry.tags.map((tag) => String(tag)) : undefined;
    dedup.set(templateId, {
      templateId,
      name: entry.name,
      status: entry.status,
      category: entry.category,
      version: Number.isNaN(versionValue) ? undefined : versionValue,
      tags,
      attachedAt: entry.attached_at,
      attachedBy: entry.attached_by,
      source: entry.rule_source,
    });
  });
  return Array.from(dedup.values());
};

export const mergeTemplateRefs = (
  existing: CanvasNodeTemplateReference[] = [],
  incoming: CanvasNodeTemplateReference[] = []
): CanvasNodeTemplateReference[] => {
  if (incoming.length === 0) {
    return existing;
  }
  const dedup = new Map<string, CanvasNodeTemplateReference>();
  existing.forEach((ref) => {
    if (ref?.templateId) {
      dedup.set(ref.templateId, ref);
    }
  });
  incoming.forEach((ref) => {
    if (!ref?.templateId) {
      return;
    }
    const current = dedup.get(ref.templateId) ?? {};
    dedup.set(ref.templateId, { ...current, ...ref });
  });
  return Array.from(dedup.values());
};

export const addMappingTemplatesToNode = (node: CanvasNode, mapping?: FieldMapping): void => {
  if (!mapping?.rule_templates || mapping.rule_templates.length === 0) {
    return;
  }
  const incoming = normaliseMappingRuleTemplates(mapping.rule_templates);
  if (incoming.length === 0) {
    return;
  }
  node.data.ruleTemplates = mergeTemplateRefs(node.data.ruleTemplates, incoming);
};

export const isTrivialDirectStep = (mapping: FieldMapping, step: TransformationStep): boolean => {
  const kind = (step.kind || '').toLowerCase();
  if (kind && kind !== 'direct') {
    return false;
  }
  if (typeof step.expression === 'string' && step.expression.trim().length > 0) {
    return false;
  }

  const mappingSourceQualified = buildQualified(mapping.source_file, mapping.source_field, mapping.source_file || 'source');
  const mappingTargetQualified = buildQualified(mapping.target_table, mapping.target_field, mapping.target_table || 'target');

  const inputs = (step.inputs ?? []).filter(Boolean)
    .map((value) => normaliseQualifiedValue(String(value)))
    .filter((q) => { const dot = q.lastIndexOf('.'); return dot === -1 || q.slice(dot + 1).trim().length > 0; });
  if (inputs.length > 1) {
    return false;
  }
  if (inputs.length === 1 && inputs[0] !== mappingSourceQualified) {
    return false;
  }

  const outputs = (step.outputs ?? []).filter(Boolean).map((value) => normaliseQualifiedValue(String(value)));
  if (outputs.length > 1) {
    return false;
  }
  if (outputs.length === 1 && outputs[0] !== mappingTargetQualified) {
    return false;
  }

  return true;
};

/** Operations that produce values without consuming source columns. */
export const INPUTLESS_OPERATIONS = new Set(['set_constant', 'set_null', 'constant', 'literal']);

export const isInputlessOperation = (step: TransformationStep): boolean => {
  const op = (step.operation || step.kind || '').toLowerCase();
  return INPUTLESS_OPERATIONS.has(op);
};

/** SQL keywords that should never be treated as column names when extracting from expressions. */
export const SQL_KEYWORDS_FOR_EXTRACTION = new Set([
  'CASE','WHEN','THEN','ELSE','END','AND','OR','NOT','IS','IN','AS',
  'NULL','TRUE','FALSE','LIKE','BETWEEN','CAST','CONCAT','COALESCE',
  'UPPER','LOWER','TRIM','LENGTH','SUBSTRING','SUBSTR','REPLACE',
  'IF','IIF','NULLIF','STRING','INT','FLOAT','DATE','TIMESTAMP',
  'SUM','AVG','COUNT','MIN','MAX','ABS','ROUND','CEIL','FLOOR',
]);

/** Extract column references from a SQL expression (CASE, CONCAT, etc.) by stripping
 *  quoted strings and SQL keywords, returning only identifiers that look like columns. */
/** Collect all source column names referenced by a mapping (deduped, order preserved). */
export const collectMappingSourceFieldNames = (mapping: FieldMapping): string[] => {
  const seen = new Set<string>();
  const fields: string[] = [];

  const addField = (raw: string | undefined | null) => {
    if (raw == null) return;
    for (const part of splitSourceField(String(raw))) {
      const { field } = parseQualifiedName(part, mapping.source_file);
      const name = (field || part).trim();
      if (!name) continue;
      const key = name.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      fields.push(name);
    }
  };

  splitSourceField(mapping.source_field).forEach(addField);

  const transformation = mapping.transformation;
  if (transformation && typeof transformation === 'object') {
    const sourceFields = (transformation as Record<string, unknown>).source_fields;
    if (Array.isArray(sourceFields)) {
      sourceFields.forEach((value) => addField(String(value)));
    }
  }

  const parameters = mapping.parameters ?? {};
  for (const key of ['columns', 'source_fields'] as const) {
    const value = parameters[key];
    if (Array.isArray(value)) {
      value.forEach((entry) => addField(String(entry)));
    }
  }

  const steps = mapping.transformation_steps ?? [];
  if (steps.length > 0) {
    for (const step of steps) {
      for (const input of normaliseInputs(mapping, step)) {
        const { field } = parseQualifiedName(input, mapping.source_file);
        addField(field || input);
      }
    }
  } else if (typeof mapping.transformation === 'string' && mapping.transformation.trim()) {
    for (const input of normaliseInputs(mapping)) {
      const { field } = parseQualifiedName(input, mapping.source_file);
      addField(field || input);
    }
  }

  return fields;
};

export const extractColumnsFromExpression = (expr: string, sourceFile?: string): string[] => {
  if (!expr) return [];
  // Strip quoted strings to avoid treating string literals as column names
  const stripped = expr.replace(/'[^']*'/g, ' ').replace(/"[^"]*"/g, ' ');
  const identifiers = stripped.match(/\b[a-zA-Z_]\w*\b/g) ?? [];
  const columns = identifiers.filter(id => !SQL_KEYWORDS_FOR_EXTRACTION.has(id.toUpperCase()));
  const unique = Array.from(new Set(columns));
  if (sourceFile) {
    return unique.map(col => buildQualified(sourceFile, col, sourceFile));
  }
  return unique;
};

export const normaliseInputs = (mapping: FieldMapping, step?: TransformationStep): string[] => {
  // Inputless operations (set_constant, set_null, constant, literal) have no source columns.
  // Return empty immediately to avoid creating malformed references like "table.".
  if (step && isInputlessOperation(step)) {
    return [];
  }

  // Phase 4.6: Primary path — use backend-parsed inputs from SqlExpressionParser
  // These are deterministic and dialect-aware, far more reliable than regex extraction.
  const parsedInputs = step?.parsed?.inputs;
  if (Array.isArray(parsedInputs) && parsedInputs.length > 0) {
    const normalised = parsedInputs
      .filter(Boolean)
      .map((value: string) => normaliseQualifiedValue(typeof value === 'string' ? value : String(value)))
      .filter((q: string) => {
        const { field } = parseQualifiedName(q);
        return field.length > 0;
      });
    if (normalised.length) {
      return normalised;
    }
  }

  // Fallback: use step.inputs (populated by backend but not from sqlglot parse)
  const inputs = step?.inputs?.filter(Boolean) ?? [];
  if (inputs.length) {
    // Filter out malformed references where the field part is empty (e.g. "tablename.")
    const normalised = inputs
      .map((value) => normaliseQualifiedValue(typeof value === 'string' ? value : String(value)))
      .filter((q) => {
        const { field } = parseQualifiedName(q);
        return field.length > 0;
      });
    if (normalised.length) {
      return normalised;
    }
    // All step inputs were malformed — fall through to mapping-level fallback
  }

  // Prefer expression-derived inputs before the single-column source_field
  // fallback — derived mappings often store only the primary column there.
  const exprText = step?.expression ?? mapping.transformation;
  if (exprText && typeof exprText === 'string' && exprText.trim()) {
    const extracted = extractColumnsFromExpression(exprText, mapping.source_file);
    if (extracted.length) return extracted;
  }

  if (mapping.source_file && mapping.source_field) {
    const rawFields = splitSourceField(mapping.source_field);
    if (rawFields.length > 0) {
      return rawFields.map(f => buildQualified(mapping.source_file, f, mapping.source_file || 'source'));
    }
    return [buildQualified(mapping.source_file, mapping.source_field, mapping.source_file)];
  }
  return [];
};
